import { IConfig } from '@qunhe/ktracker-types';
import { DefTypes } from './constants';
import {
  sendError as _sendError,
  sendErrorV2 as _sendErrorV2,
  sendEvent as _sendEvent,
  sendEventV2 as _sendEventV2,
} from './monitor/business';
import collectError from './monitor/exception';
import { globalErrorHandler } from './monitor/exception/globalError';
import { globalUnhandledRejectionHandler } from './monitor/exception/unhandledRejection';
import collectData from './monitor/performance';
import Ajax from './utils/ajax';
import {
  currentVisitId,
  getCurrentTimestamp,
  getCurrentUserId,
  getDeviceToken,
  getTraceId,
  getType,
} from './utils/func';
import logger from './utils/logger';
import SystemOptions, { initOptions } from './utils/options';

const Ktracker = (function() {
  // performance 是否开启标志
  let performanceFlag: boolean = false;
  // exception 是否开启标志
  let exceptionFlag: boolean = false;
  window.g_ktrackerTiming = { };

  const g_options = initOptions();

  function checkAvailable() {
    return (
      !!window.performance && !!window.addEventListener && !!MutationObserver && !!XMLHttpRequest
    );
  }

  function init(type?: string) {
    if (type === 'auto' && !g_options.autoInit) {
      // tslint:disable-next-line:max-line-length
      logger.warn('You need to start Ktracker with init function manually, because of the autoInit option was turned off.');
      return;
    }
    window.g_ktrackerTiming.initStart = getCurrentTimestamp();
    if (checkAvailable()) {
      logger.primary('Ktracker mounted');

      if (!SystemOptions.getInstance().getVisitId()) {
        SystemOptions.getInstance().setVisitId(currentVisitId);
      }
      if (!SystemOptions.getInstance().getCurrentLogCount()) {
        SystemOptions.getInstance().setCurrentLogCount(0);
      }

      initException();
      initPagePerformance();
    } else {
      logger.warn('your browser is too old');
    }
    window.g_ktrackerTiming.initEnd = getCurrentTimestamp();
  }

  /**
   * 初始化异常监控
   */
  function initException() {
    window.g_ktrackerTiming.exceptionMonitorInitStart = getCurrentTimestamp();
    if (exceptionFlag || !g_options.initException) {
      logger.info('exception collector unloaded');
      return;
    }

    collectError(g_options);
    exceptionFlag = true;
    logger.success('exception collector loaded');
    window.g_ktrackerTiming.exceptionMonitorInitEnd = getCurrentTimestamp();
  }

  /**
   * 初始化性能监控
   */
  function initPagePerformance() {
    window.g_ktrackerTiming.performanceMonitorInitStart = getCurrentTimestamp();
    if (!window.performance || performanceFlag || !g_options.initPagePerformance) {
      logger.info('performance collector unloaded');
      return;
    }

    const onloadHandler = () => {
      if ((window as any).requestIdleCallback) {
        (window as any).requestIdleCallback(collectData);
      } else {
        setTimeout(collectData, g_options.performanceDelay);
      }
    };

    window.addEventListener('load', onloadHandler, false);
    performanceFlag = true;
    logger.success('performance collector loaded');
    window.g_ktrackerTiming.performanceMonitorInitEnd = getCurrentTimestamp();
  }

  async function send(
    data: any,
    url: string,
    onSuccess?: (res: any) => void,
    onError?: (error: any) => void,
  ) {
    try {
      const res = Ajax.send(data, url);
      if (onSuccess && getType(onSuccess) === DefTypes.FUNC) {
        onSuccess(res);
      }
    } catch (error) {
      if (onError && getType(onError) === DefTypes.FUNC) {
        onError(error);
      }
    }
  }

  async function sendEvent(
    eventType: any,
    data: any,
    options?: IConfig | null | { },
    onSuccess?: (res: any) => void,
    onError?: (error: any) => void,
  ) {
    if (!options) {
      options = { };
    }
    await _sendEvent(eventType, data, options, onSuccess, onError);
  }

  async function sendEventV2(
    params: {
      eventType: any,
      data: any,
      labels?: {
        [key: string]: string,
      },
      level?: string,
    },
    options?: IConfig | { } | null,
    onSuccess?: (res: any) => void,
    onError?: (res: any) => void,
  ) {
    if (!options) {
      options = { };
    }
    await _sendEventV2(params, options, onSuccess, onError);
  }

  async function sendError(error: Error, options?: IConfig | null | { }) {
    if (!options) {
      options = { };
    }
    await _sendError(error, options);
  }

  async function sendErrorV2(
    params: {
      error: Error,
      labels?: {
        [key: string]: string,
      },
      level?: string,
    },
    options?: IConfig | null | { },
  ) {
    if (!options) {
      options = { };
    }
    await _sendErrorV2(params, options);
  }

  function uninstall() {
    if (exceptionFlag) {
      uninstallException();
    }
    if (performanceFlag) {
      uninstallPerformance();
    }
  }

  function uninstallException() {
    if (exceptionFlag) {
      exceptionFlag = false;
      SystemOptions.getInstance().setCurrentLogCount(0);

      if (getType(globalErrorHandler) === DefTypes.FUNC) {
        window.removeEventListener('error', globalErrorHandler, false);
      }

      if (getType(globalUnhandledRejectionHandler) === DefTypes.FUNC) {
        window.removeEventListener('unhandledrejection', globalUnhandledRejectionHandler, false);
      }

      logger.success('exception collector destroyed');
    }
  }

  function uninstallPerformance() {
    if (performanceFlag) {
      performanceFlag = false;
      logger.success('performance collector destroyed');
    }
  }

  return {
    init,
    initException,
    initPagePerformance,
    send,
    sendError,
    sendErrorV2,
    sendEvent,
    sendEventV2,
    uninstall,
    uninstallException,
    uninstallPerformance,
    userId: getCurrentUserId(),
    visitId: currentVisitId,
    generateHunterid: getTraceId,
    deviceId: getDeviceToken(g_options.deviceTokenKey),
  };
}());

// 初始化代码
window.Ktracker = Ktracker;
Ktracker.init('auto');

export default Ktracker;
